import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import CustomDropdown from '../../custom/customDropdown';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Loading from '../../Loading/loading';
import { AuthContext } from '../../Header/auth - context';
import { useSelector } from 'react-redux';
/* Sva pravila i tekstovi dolaze iz zajedničkog modula — isti izvor koji koristi
   i DIJAGRAM AKTIVNOSTI, pa se dva prikaza ne mogu razići. */
import { AETR_GRUPE, AETR_LABELE, izracunajPrekrsaje, pregledIstekaKartica, danPrekrsaja, daniPrekrsaja } from './aetrPrekrsaji';
import { propisiZaDrzavu } from './propisi';
import { opsegZaUpit } from './vreme';
import { NIVOI_LISTA, KAZNE_PREGLED, KAZNE_VAN_OBRACUNA } from './kazneIznosi';
import KazneEvidencija from './KazneEvidencija';
import KazneIspisi, { putanjaFajla } from './KazneIspisi';
import './Kazne.css';

/* PREGLED = lista prekršaja, KAZNE = obrada (u izradi), ISPISI = ispisi tahografa
   sa telefona (task 108), IZNOSI = šifarnik kazni */
const PODTABOVI = ['PREGLED', 'KAZNE', 'ISPISI', 'IZNOSI'];

/* Dani (YYYY-MM-DD) na koje se prekršaj odnosi — dan pripisivanja i svi dani koje
   interval dodiruje. Ispis ili kazna sa telefona za bilo koji od njih ga objašnjava. */
const daniReda = (x) => {
  const dan = danPrekrsaja(x);
  const s = new Set(daniPrekrsaja({
    _od: x.Pocetak ? Date.parse(x.Pocetak) : null,
    _do: x.Kraj ? Date.parse(x.Kraj) : null,
    _day: dan,
  }));
  if (dan) s.add(dan);
  return s;
};

/* Task 108 — prekršaj je „objašnjen" (zelen) kad za istog vozača i isti dan postoji
   ispis tahografa ili kazna poslata sa telefona. Prekršaji se ne čuvaju u bazi,
   pa je jedina veza vozač + dan. */
const spojiObjasnjenja = (redovi, ispisi, kazne) => {
  const poKljucu = new Map();
  const dodaj = (tip, o) => {
    const k = `${o.id_zaposleni}|${o.Datum}`;
    if (!poKljucu.has(k)) poKljucu.set(k, []);
    poKljucu.get(k).push({ ...o, _tip: tip });
  };
  (ispisi || []).forEach(o => dodaj('ISPIS', o));
  (kazne || []).forEach(o => dodaj('KAZNA', o));
  if (!poKljucu.size) return redovi;
  return redovi.map(x => {
    if (x._sep || x.EmployeeId == null) return x;
    const nadjeno = [];
    daniReda(x).forEach(d => (poKljucu.get(`${x.EmployeeId}|${d}`) || []).forEach(o => nadjeno.push(o)));
    return nadjeno.length ? { ...x, _obj: nadjeno } : x;
  });
};

const dinari = (n) => `${n.toLocaleString('sr-RS')} RSD`;

/** Vrednost u izborniku kad se gledaju svi propisi odjednom. */
const SVI = 'SVI';

/* Spisak vrsta prekršaja zavisi od izabranog propisa — nema smisla nuditi
   „Dnevno radno vreme" kad se kontroliše samo AETR, jer se tada i ne računa. */
const faultOptions = (t, propis, idDrzava) => {
  const vaze = propisiZaDrzavu(idDrzava).map(p => p.kljuc);
  const dozvoljeni = propis === SVI
    ? AETR_GRUPE.filter(g => vaze.includes(g.propis))
    : AETR_GRUPE.filter(g => g.propis === propis);
  return [
    { label: t('mk.sveKazne'), value: 'all' },
    ...dozvoljeni.map(g => ({ label: g.label, value: g.key })),
  ];
};

/* Interni propisi Srbije se ne nude firmi van Srbije — vidi propisi.js, polje
   `drzave`. Zato izbornik zavisi od id_drzava firme. */
const propisOptions = (t, idDrzava) => [
  { label: t('mk.sviPropisi'), value: SVI },
  ...propisiZaDrzavu(idDrzava).map(p => ({ label: t(p.i18nKljuc), value: p.kljuc })),
];

function withSeparators(groups) {
  const out = [];
  let counter = 0;
  groups.forEach(({ key, data }) => {
    if (data.length === 0) return;
    out.push({ _sep: true, Prekršaj: '', Vozač: AETR_LABELE[key], Kompanija: '', Poruka: '' });
    data.forEach(x => {
      counter++;
      out.push({ ...x, Prekršaj: counter });
    });
  });
  return out;
}

/* Kad stigne kroz propse (iz KazneIzvestaji), tab koristi ZAJEDNIČKI filter
   (od/do datuma i vozač) sa ostalih tabova i sam povlači sve kazne. */
const KazneKazne = ({ dateFrom: propOd, dateTo: propDo, selectedCard: propVozac, spoljniFilter = false }) => {
  const { t } = useTranslation();
  const toast   = useRef(null);
  const auth    = useContext(AuthContext);
  const company = useSelector(state => state.company);

  const toISO = d => d instanceof Date
    ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
    : (d || '');

  const [lokalniOd,    setDateFrom]     = useState(new Date(Date.now() - 30 * 86400000));
  const [lokalniDo,    setDateTo]       = useState(new Date());
  const [cards,        setCards]        = useState([]);
  const [lokalniVozac, setSelectedCard] = useState(null);
  const [faultType,    setFaultType]    = useState('all');
  /* Koji se propis kontroliše. Podrazumevano SVI, da izbor ničim ne sakrije
     prekršaje koji su se do sada videli. */
  const [propis,       setPropis]       = useState(SVI);
  /* id_drzava firme (auth.firme_subscription). Odlucuje koji propisi uopste vaze. */
  const idDrzava = company?.companyCountryID ?? null;

  // spoljni filter (zajednički sa ostalim tabovima) ima prednost nad lokalnim
  const dateFrom     = spoljniFilter ? propOd    : lokalniOd;
  const dateTo       = spoljniFilter ? propDo    : lokalniDo;
  const selectedCard = spoljniFilter ? propVozac : lokalniVozac;
  const [tableData,    setTableData]    = useState([]);
  const [load,         setLoad]         = useState(false);
  const [podtab,       setPodtab]       = useState('PREGLED');

  useEffect(() => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/cards', {
      headers: { Authorization: 'Bearer ' + auth.token },
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCards(data.map(c => ({
            label: c.HolderName || c.CardNumber,
            value: c.EmployeeId,
          })));
        }
      })
      .catch(() => {});
  }, [auth.token]);

  /* Sa zajedničkim filterom se sve kazne povlače same — bez klika na "Prikaži"
     i uz zadržan izbor perioda i vozača sa prethodnog taba. */
  useEffect(() => {
    if (!spoljniFilter || !dateFrom || !dateTo) return;
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spoljniFilter, propOd, propDo, propVozac, faultType]);

  const handleSearch = async () => {
    if (!dateFrom || !dateTo) {
      toast.current.show({ severity: 'warn', summary: 'Upozorenje', detail: 'Unesite opseg datuma', life: 3000 });
      return;
    }
    setLoad(true);
    setTableData([]);
    try {
      /* Upit ide dan širi sa svake strane: prekršaj po lokalnom vremenu može
         da leži u UTC danu van traženog opsega (lokalni 30.06 23:00 je UTC 30.06
         21:00, ali lokalni 01.06 00:30 je UTC 31.05 22:30). Prikaz ostaje na
         traženom opsegu kroz `prikazOd`/`prikazDo`. */
      const body = opsegZaUpit(toISO(dateFrom), toISO(dateTo));
      if (selectedCard) body.employeeId = selectedCard;

      const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/faults', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.current.show({ severity: 'error', summary: 'Greška', detail: data.message || 'Greška pri učitavanju', life: 4000 });
        setLoad(false);
        return;
      }

      const { records, cards: cardList } = data;
      const companyName = company?.companyName ?? '';

      /* Filter "Istek kartica" je pregled svih kartica (i onih koje tek ističu),
         dok se u "Sve kazne" broje samo one koje su STVARNO istekle. */
      if (faultType === 'cardExpiry') {
        const res = pregledIstekaKartica({ cards: cardList, companyName });
        setTableData(res.map((x, i) => ({ ...x, Prekršaj: i + 1 })));
        setLoad(false);
        return;
      }

      if (!records || records.length === 0) {
        toast.current.show({ severity: 'info', summary: 'Info', detail: 'Nema aktivnosti za odabrani period', life: 3000 });
        setLoad(false);
        return;
      }

      /* Backend je povukao i aktivnosti pre traženog datuma (kontekst za pravila
         koja gledaju nedeljama unazad) — prikazuju se samo prekršaji od prikazOd. */
      const grupe = izracunajPrekrsaje({
        records, cards: cardList, companyName, dateTo,
        prikazOd: toISO(dateFrom),
        prikazDo: toISO(dateTo),
        propisi: propis === SVI ? null : [propis],
        idDrzava,
      });

      const redovi = faultType === 'all'
        ? withSeparators(grupe)
        : ((grupe.find(g => g.key === faultType)?.data) || []).map((x, i) => ({ ...x, Prekršaj: i + 1 }));

      /* Ispisi i kazne sa telefona za isti period. Ako ne stignu, prekršaji se
         i dalje prikazuju — samo bez zelenog. */
      let ispisi = [], kazneTel = [];
      try {
        const ri = await fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/ispisi', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
          body: JSON.stringify({ step: 'WEB_GET', od: body.dateFrom || toISO(dateFrom), do: body.dateTo || toISO(dateTo),
                                 id_zaposleni: selectedCard || null }),
        });
        if (ri.ok) {
          const di = await ri.json();
          ispisi = di.IspisiDB || [];
          kazneTel = di.KazneDB || [];
        }
      } catch { /* bez spajanja */ }
      setTableData(spojiObjasnjenja(redovi, ispisi, kazneTel));
    } catch (err) {
      console.error('KazneKazne error:', err);
      toast.current.show({ severity: 'error', summary: 'Greška', detail: 'Greška pri obradi podataka', life: 4000 });
    }
    setLoad(false);
  };

  const exportExcel = async () => {
    if (!tableData || tableData.length === 0) return;
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Kazne AETR');
    const headerRow = worksheet.addRow(['#', 'Vozač', 'Kompanija', 'Poruka', t('mk.objasnjenje')]);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    tableData.forEach(row => {
      if (row._sep) {
        const sepRow = worksheet.addRow(['', row.Vozač, '', '']);
        sepRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        sepRow.eachCell(cell => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
        });
      } else {
        const r = worksheet.addRow([row.Prekršaj, row.Vozač, row.Kompanija, row.Poruka,
          (row._obj || []).map(o => `${o._tip === 'ISPIS' ? t('mk.objIspis') : t('mk.objKazna')} ${o.DatumPrikaz}: ${o.Opis || ''}`).join('; ')]);
        r.eachCell(cell => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
      }
    });
    worksheet.columns = [{ width: 6 }, { width: 26 }, { width: 26 }, { width: 80 }, { width: 50 }];
    const serbianDateTime = new Date().toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
    }).replace(/\s/g, ' ').replace(/,/g, '');
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Kazne AETR - ${company?.companyName ?? ''} - ${serbianDateTime}.xlsx`);
  };

  const rowClass = row => row._sep ? 'aetr-separator' : (row._obj?.length ? 'aetr-objasnjen' : '');

  /* Ispis / kazna sa telefona koji objašnjava prekršaj — oznaka, opis i slika. */
  const objasnjenjeBody = (r) => {
    if (r._sep || !r._obj?.length) return null;
    return (
      <div className="aetr-obj-lista">
        {r._obj.map(o => {
          const prvi = String(o.Fajlovi || '').split('|').filter(Boolean)[0];
          return (
            <div key={`${o._tip}-${o.id}`} className="aetr-obj">
              <span className="aetr-obj-tip">
                <i className={o._tip === 'ISPIS' ? 'pi pi-file' : 'pi pi-exclamation-triangle'} />{' '}
                {o._tip === 'ISPIS' ? t('mk.objIspis') : t('mk.objKazna')} {o.DatumPrikaz}
              </span>
              {!!o.Opis && <span className="aetr-obj-opis">{o.Opis}</span>}
              {!!prvi && (
                <Button icon="pi pi-eye" className="p-button-text p-button-sm" type="button"
                  onClick={() => window.open(putanjaFajla(prvi), '_blank')}
                  tooltip={t('taho.pregledajDokument')} tooltipOptions={{ position: 'top' }} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const header = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <Tooltip target=".kazne-aetr-xls" position="bottom" />
        <Button type="button" icon="pi pi-file-excel" severity="success" rounded
          onClick={exportExcel} className="kazne-aetr-xls" data-pr-tooltip="Export XLS" />
      </div>
    </div>
  );

  return (
    <>
      {load && <Loading />}
      <Toast ref={toast} />

      <div className="kazne-wh-tabs an-podtabovi">
        {PODTABOVI.map(p => (
          <button key={p}
            className={'kazne-wh-tab' + (podtab === p ? ' kazne-wh-tab--active' : '')}
            onClick={() => setPodtab(p)}>
            {t(`kaznePodtab.${p}`)}
          </button>
        ))}
      </div>

      {podtab === 'PREGLED' && (
      <>
      <div className="kazne-izv-filters" style={{ borderTop: '1px solid #e5e7eb', marginTop: 0 }}>
        <div className="kazne-izv-filter-row">
          {/* Datum i vozač se ne ponavljaju kad dolaze iz zajedničkog filtera */}
          {!spoljniFilter && (
            <>
              <label className="kazne-izv-label">{t('mk.odDatuma')}</label>
              <Calendar
                value={dateFrom}
                onChange={e => setDateFrom(e.value)}
                showIcon
                dateFormat="dd.mm.yy"
                mask="99.99.9999"
              />
              <label className="kazne-izv-label">{t('mk.doDatuma')}</label>
              <Calendar
                value={dateTo}
                onChange={e => setDateTo(e.value)}
                showIcon
                dateFormat="dd.mm.yy"
                mask="99.99.9999"
              />
              <label className="kazne-izv-label">{t('ture.vozac')}</label>
              <CustomDropdown
                value={selectedCard}
                options={cards}
                onChange={e => setSelectedCard(e.target.value)}
                optionLabel="label"
                placeholder={t('ture.sviVozaci')}
                className="kazne-izv-dropdown"
              />
            </>
          )}
          <label className="kazne-izv-label">{t('mk.propis')}</label>
          <CustomDropdown
            value={propis}
            options={propisOptions(t, idDrzava)}
            onChange={e => { setPropis(e.target.value); setFaultType('all'); }}
            optionLabel="label"
            showClear={false}
            className="kazne-izv-dropdown"
            style={{ minWidth: '200px' }}
          />
          <label className="kazne-izv-label">{t('mk.tipKazne')}</label>
          <CustomDropdown
            value={faultType}
            options={faultOptions(t, propis, idDrzava)}
            onChange={e => setFaultType(e.target.value)}
            optionLabel="label"
            placeholder={t('mk.sveKazne')}
            showClear={false}
            className="kazne-izv-dropdown"
            style={{ minWidth: '260px' }}
          />
          <Button label={t('mk.prikazi')} icon="pi pi-search" onClick={handleSearch} className="p-button p-button-primary" />
        </div>
      </div>

      <div className="zaposleni_table_container" style={{ marginTop: '1%' }}>
        <DataTable
          value={tableData}
          paginator
          rows={50}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage={t('common.noData')}
          size="small"
          scrollable
          scrollHeight="55vh"
          resizableColumns
          columnResizeMode="expand"
          className="turePregledTable stavke-tabela"
          header={header}
          rowClassName={rowClass}
        >
          <Column field="Prekršaj"  header="#"         sortable style={{ minWidth: '60px' }}
            body={r => r._sep ? null : r.Prekršaj} />
          <Column field="Vozač"     header={t('ture.vozac')}     sortable style={{ minWidth: '160px' }}
            body={r => r._sep ? <strong style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>{r.Vozač}</strong> : r.Vozač} />
          <Column field="Kompanija" header={t('mk.kompanija')} sortable style={{ minWidth: '160px' }}
            body={r => r._sep ? null : r.Kompanija} />
          <Column field="Poruka"    header={t('mk.poruka')}    style={{ minWidth: '400px' }}
            body={r => r._sep ? null : r.Poruka} />
          <Column header={t('mk.objasnjenje')} style={{ minWidth: '260px' }} body={objasnjenjeBody} />
        </DataTable>
      </div>
      </>
      )}

      {podtab === 'KAZNE' && <KazneEvidencija />}

      {podtab === 'ISPISI' && <KazneIspisi />}

      {podtab === 'IZNOSI' && (
        /* Sve tri tabele imaju svoj scroll; ceo panel takođe, da poslednja
           tabela ne izađe iz ekrana na nižim rezolucijama. */
        <div className="zaposleni_table_container kazne-iznosi-panel" style={{ marginTop: '1%' }}>
          {/* Šifarnik: 4 nivoa težine sa fiksnim iznosima */}
          <h4 className="kazne-iznosi-naslov">{t('kaznePodtab.nivoiNaslov')}</h4>
          <DataTable value={NIVOI_LISTA} size="small"
            resizableColumns columnResizeMode="expand" className="turePregledTable stavke-tabela"
            emptyMessage={t('common.noData')}>
            <Column field="nivo" header={t('kaznePodtab.nivo')} style={{ minWidth: '70px' }} />
            <Column header={t('kaznePodtab.pravnoLice')} style={{ minWidth: '130px' }}
              body={r => dinari(r.pravno)} />
            <Column header={t('kaznePodtab.preduzetnik')} style={{ minWidth: '130px' }}
              body={r => dinari(r.preduzetnik)} />
            <Column header={t('kaznePodtab.odgovornoLice')} style={{ minWidth: '130px' }}
              body={r => dinari(r.odgovorno)} />
            <Column header={t('ture.vozac')} style={{ minWidth: '130px' }}
              body={r => dinari(r.vozac)} />
          </DataTable>

          {/* Koji prekršaj nosi koji nivo */}
          <h4 className="kazne-iznosi-naslov" style={{ marginTop: '1.5rem' }}>
            {t('kaznePodtab.prekrsajiNaslov')}
          </h4>
          <DataTable value={KAZNE_PREGLED} size="small"
            resizableColumns columnResizeMode="expand" className="turePregledTable stavke-tabela"
            emptyMessage={t('common.noData')} rowGroupMode="subheader" groupRowsBy="grupa"
            sortField="grupa" sortOrder={0}
            rowGroupHeaderTemplate={r => <strong>{r.grupa}</strong>}>
            <Column field="opis" header={t('kaznePodtab.prekrsaj')} style={{ minWidth: '380px' }} />
            <Column field="osnov" header={t('kaznePodtab.osnov')} style={{ minWidth: '260px' }} />
            <Column header={t('kaznePodtab.nivo')} style={{ minWidth: '120px' }}
              body={r => (typeof r.nivo === 'number'
                ? r.nivo
                : <span title={t('kaznePodtab.objasnjenjeRaspon')}>{r.nivoi || ''}</span>)} />
            <Column header={t('kaznePodtab.iznosi')} style={{ minWidth: '340px' }}
              body={r => {
                if (typeof r.nivo !== 'number') return r.napomena || '';
                const n = NIVOI_LISTA.find(x => x.nivo === r.nivo);
                return n ? `${dinari(n.pravno)} / ${dinari(n.preduzetnik)} / ${dinari(n.odgovorno)} / ${dinari(n.vozac)}` : '';
              }} />
          </DataTable>
          <div className="kazne-iznosi-napomena">{t('kaznePodtab.objasnjenjeRaspon')}</div>

          {/* Nivo 1 se iz DDD podataka ne može utvrditi — navodi se kao referenca */}
          <h4 className="kazne-iznosi-naslov" style={{ marginTop: '1.5rem' }}>
            {t('kaznePodtab.vanObracunaNaslov')}
          </h4>
          <DataTable value={KAZNE_VAN_OBRACUNA} size="small"
            resizableColumns columnResizeMode="expand" className="turePregledTable stavke-tabela"
            emptyMessage={t('common.noData')}>
            <Column field="opis" header={t('kaznePodtab.prekrsaj')} style={{ minWidth: '460px' }} />
            <Column field="osnov" header={t('kaznePodtab.osnov')} style={{ minWidth: '240px' }} />
            <Column header={t('kaznePodtab.nivo')} style={{ minWidth: '80px' }} body={() => 1} />
            <Column header={t('kaznePodtab.iznosi')} style={{ minWidth: '340px' }}
              body={() => {
                const n = NIVOI_LISTA[0];
                return `${dinari(n.pravno)} / ${dinari(n.preduzetnik)} / ${dinari(n.odgovorno)} / ${dinari(n.vozac)}`;
              }} />
          </DataTable>
          <div className="kazne-iznosi-napomena">{t('kaznePodtab.vanObracunaNapomena')}</div>

          <div className="kazne-iznosi-napomena">{t('kaznePodtab.napomenaIzvor')}</div>
        </div>
      )}
    </>
  );
};

export default KazneKazne;
